import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NightTrainNearbyOption, NightTrainSearchSummary, NightTrainsNearby, searchNightTrainsNearby } from '../../journeys/api';
import type { NightTrainStatus } from '../nightTrain';
import { isNightTrainMode } from '../../../shared/transport';
import { getModeLabel, getTransportEmoji } from '../../../shared/transport';
import { cityOnly } from '../utils';
import { Dots } from './RouteTimeline';
import { TrustMark } from './NightTrainSheetBlock';
import {
  NEARBY_LOSS_NOTE, bedsChip, bedsHedge, bedsList, boardLine, cardRunsNote, getOffLine, nearbyChangeNote, nearbyDistanceNote,
  nearbyEmptyNote, nearbyNoDirectNote, nearbyReplacesNote, nearbyReview, nearbyRules, nearbyTooShortNote, productLine, radiusHasTrains, runsLine,
  fmtRide, searchBlockedReason, searchCountLabel, unavailableReason,
} from '../nightTrain';

// "Search night trains nearby" (docs/product/night-trains/README.md, slice 6).
// For a part of the trip: the night trains that stop close to both of its
// places, in three radius steps. The ONE place this search lives: every entry
// point (transport lists, leg sheet, leg cards, journey summary) asks the
// page to open it (NightTrainSearchButton).
//
// Three views in one dialog (Paulo 2026-09-29):
//  0. Which part of the trip, when the dialog is opened for the journey and
//     not for one part of it (the summary's night pill): every part, with
//     how it is travelled today and how many night trains the search would
//     find for it. A part it finds none for says so and cannot be picked
//     (the backend looked when it read the journey: NightTrainSearchSummary).
//     Opened from a part of the trip, the dialog starts at 1.
//  1. The list, to scan: each train is a closed row with its two stations,
//     its times, chips for what sets it apart, and "Next" always in view.
//     "Details" opens the rest; one row is open at a time. It starts at the
//     first radius that finds a night train; a radius that finds none is
//     not offered.
//  2. The review, to decide: the part of the trip as it will be, drawn top
//     to bottom like the journey page (places and how to get from one to the
//     next), what it replaces, and "Add to my trip". It replaces a worded
//     confirmation that was hard to read.
//
// A centered modal above the leg sheet, like CompareTransportDialog (z 2800;
// Escape goes back from the review, then closes). Everything shown comes
// from the timetable data (backend services/nightTrains/nearby.js); the copy
// from ../nightTrain.ts. Distances are by road. The only estimates are the
// times of the stretches that are not by night train, marked with an
// asterisk.

export interface NightTrainsNearbyDialogProps {
  open: boolean;
  journeyId: string | null;
  /** The part of the trip searched. Null: the traveller picks one first. */
  legId: string | null;
  /** The parts of the trip, in order, as they are travelled today. */
  legs?: NearbyLegChoice[];
  onPickLeg?: (legId: string | null) => void;
  /** Add the picked train to the journey. Resolve true when it was added. */
  onAdd: (legId: string, option: NightTrainNearbyOption) => Promise<boolean>;
  onClose: () => void;
}

export interface NearbyLegChoice {
  id: string;
  /** "Milan → Hamburg" */
  label: string;
  transportMode?: string | null;
  /** Whether a night train can be searched for it, and why not. */
  status?: NightTrainStatus | null;
  /** What the search would find for it; null when that was not worked out. */
  search?: NightTrainSearchSummary | null;
  booked?: boolean;
}

const INK = '#16241d';
const MUTED = '#6c7872';
const HAIRLINE = 'rgba(20,40,30,0.08)';
const NIGHT = '#3b3c64';
const GREEN = '#157a55';

type ChipTone = 'plain' | 'night' | 'warn' | 'current';
const CHIP_TONES: Record<ChipTone, { background: string; color: string }> = {
  plain: { background: '#eef1ee', color: '#4b5a53' },
  night: { background: '#ecebf7', color: NIGHT },
  warn: { background: '#fbf3df', color: '#8a6a14' },
  current: { background: '#e7f1ec', color: '#0e5c40' },
};
const Chip: React.FC<{ tone?: ChipTone; children: React.ReactNode }> = ({ tone = 'plain', children }) => (
  <span style={{ ...CHIP_TONES[tone], fontSize: 10.5, fontWeight: 700, lineHeight: 1.25, borderRadius: 7, padding: '3px 7px' }}>{children}</span>
);

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden style={{ flex: 'none', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}>
    <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// One detail of an open row: a quiet label, the fact, and what goes with it.
const Detail: React.FC<{ label: string; value: React.ReactNode; note?: React.ReactNode; tone?: 'warn' }> = ({ label, value, note, tone }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '68px minmax(0, 1fr)', columnGap: 10, padding: '7px 0' }}>
    <span style={{ fontSize: 11.5, fontWeight: 600, color: MUTED, paddingTop: 1 }}>{label}</span>
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: tone === 'warn' ? '#9c7b22' : INK }}>{value}</span>
      {note && <span style={{ display: 'block', fontSize: 12, color: MUTED, marginTop: 1 }}>{note}</span>}
    </span>
  </div>
);

const primaryButton: React.CSSProperties = { font: 'inherit', fontSize: 12.5, fontWeight: 700, borderRadius: 12, padding: '8px 14px', background: GREEN, color: '#fff', border: 'none', whiteSpace: 'nowrap' };

const NightTrainsNearbyDialog: React.FC<NightTrainsNearbyDialogProps> = ({ open, journeyId, legId, legs, onPickLeg, onAdd, onClose }) => {
  const [radiusKm, setRadiusKm] = useState<number | null>(null);
  const [result, setResult] = useState<NightTrainsNearby | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [reviewing, setReviewing] = useState<NightTrainNearbyOption | null>(null);
  const [adding, setAdding] = useState(false);
  const requestRef = useRef(0);
  const reviewingRef = useRef(false);
  reviewingRef.current = reviewing != null;
  // The open row is brought into view, so nothing of it is below the fold.
  const openRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (openKey && openRowRef.current) openRowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openKey]);

  // Another part of the trip starts again from the first radius.
  useEffect(() => { setRadiusKm(null); setResult(null); setOpenKey(null); }, [legId]);

  // A fresh search whenever the dialog opens, or the part of the trip or the radius changes.
  useEffect(() => {
    if (!open) {
      setRadiusKm(null);
      setResult(null);
      setOpenKey(null);
      setRulesOpen(false);
      setReviewing(null);
      setError(false);
      return;
    }
    if (!journeyId || !legId) return;
    const request = ++requestRef.current;
    setLoading(true);
    setError(false);
    setReviewing(null);
    searchNightTrainsNearby(journeyId, legId, radiusKm ?? undefined)
      .then((r) => { if (requestRef.current === request) { setResult(r); setOpenKey(null); } })
      .catch(() => { if (requestRef.current === request) setError(true); })
      .finally(() => { if (requestRef.current === request) setLoading(false); });
  }, [open, journeyId, legId, radiusKm]);

  // Dialog-first Escape (capture phase), so the leg sheet under it stays
  // open: back from the review first, then close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (reviewingRef.current) setReviewing(null); else onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  const choices = legs || [];
  const picking = !legId;
  const fallback = choices.find((l) => l.id === legId)?.label || '';
  // The places as the search knows them (a night train that came with stops
  // stands for the whole stretch between the places around them).
  const fromCity = result ? cityOnly(result.from) : '';
  const toCity = result ? cityOnly(result.to) : '';
  const heading = result ? `${fromCity} → ${toCity}` : fallback;
  const radii = result?.radiiKm || [];
  const activeRadius = radiusKm ?? result?.radiusKm ?? null;
  const options = result?.options || [];
  const tooShort = Boolean(result && result.status !== 'ok');
  const switchable = Boolean(onPickLeg && choices.length > 1 && !reviewing && !picking);
  // Nothing at any radius: said for the widest one.
  const widest = radii.length ? Math.max(...radii) : null;
  const nothingAnywhere = Boolean(result && radii.length && radii.every((r) => !radiusHasTrains(result.counts, r)));
  const rules = result ? nearbyRules(result.rules) : [];
  const hasEstimate = (o: NightTrainNearbyOption | null) => Boolean(o
    && ((o.changeAtBoarding && o.toBoardingMinutes != null) || (o.changeAtArrival && o.fromArrivalMinutes != null)));
  const opened = options.find((o) => o.key === openKey) || null;
  // None of the trains listed is direct (boarded and left in the two places
  // themselves): said above the list.
  const noneDirect = options.length > 0 && options.every((o) => o.changeAtBoarding || o.changeAtArrival);

  const add = async () => {
    if (!reviewing || !legId) return;
    setAdding(true);
    const added = await onAdd(legId, reviewing);
    setAdding(false);
    if (added) onClose();
  };

  const rulesBlock = (startOpen: boolean) => (
    <div style={{ marginTop: 10 }}>
      {!startOpen && (
        <button
          type="button"
          onClick={() => setRulesOpen((o) => !o)}
          aria-expanded={rulesOpen}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, fontWeight: 700, color: GREEN, cursor: 'pointer' }}
        >
          Which night trains are shown? <Chevron open={rulesOpen} />
        </button>
      )}
      {(startOpen || rulesOpen) && (
        <div style={{ marginTop: startOpen ? 0 : 7, background: '#f7f9f7', border: `1px solid ${HAIRLINE}`, borderRadius: 12, padding: '10px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 5 }}>A night train is shown when:</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 5 }}>
            {rules.map((rule) => (
              <li key={rule} style={{ display: 'flex', gap: 7, fontSize: 12, lineHeight: 1.4, color: '#4b5a53' }}>
                <span aria-hidden style={{ color: GREEN, fontWeight: 800, flex: 'none' }}>•</span>
                <span>{rule}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // Every part of the trip, with how it is travelled today. One the search
  // cannot help with says why, and cannot be picked.
  const picker = (
    <>
      <div style={{ overflowY: 'auto', minHeight: 56, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {choices.map((l) => {
          const night = isNightTrainMode(l.transportMode);
          // Why this part cannot be picked: its places are too close, or the
          // search would find nothing for it.
          const reason = !night && l.status === 'too_short' ? unavailableReason('too_short') : searchBlockedReason(l.search, night);
          const off = reason != null;
          // Found: how many. A night train in use counts the others, under its own chip.
          const found = searchCountLabel(l.search, night);
          const today = `${getModeLabel(l.transportMode)} today${l.booked ? ', booked' : ''}`;
          return (
            <button
              key={l.id}
              type="button"
              disabled={off}
              onClick={() => onPickLeg && onPickLeg(l.id)}
              style={{
                flex: 'none', display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', font: 'inherit',
                border: `1px solid ${HAIRLINE}`, borderRadius: 14, background: off ? '#fafbfa' : '#fff', padding: '11px 13px', cursor: off ? 'default' : 'pointer',
              }}
            >
              <span aria-hidden style={{ fontSize: 18, lineHeight: 1, flex: 'none', opacity: off ? 0.45 : 1 }}>{getTransportEmoji(l.transportMode) || '🚏'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: INK, opacity: off ? 0.45 : 1 }}>{l.label}</span>
                <span style={{ display: 'block', fontSize: 12, color: MUTED, marginTop: 1 }}>
                  {reason || (night && found ? `${found} found${l.booked ? ', booked' : ''}` : today)}
                </span>
              </span>
              {/* "selected", so it reads as how this part is travelled, not as a label of the row. */}
              {night && <Chip tone="night">Night train selected</Chip>}
              {!night && !off && found && <Chip tone="night">{found}</Chip>}
              {!off && <span aria-hidden style={{ color: '#b3bbb6', fontSize: 16, flex: 'none' }}>›</span>}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{ width: '100%', marginTop: 11, background: '#f4f6f3', color: MUTED, border: '1px solid rgba(20,40,30,0.06)', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >
        Close
      </button>
    </>
  );

  const list = (
    <>
      {/* The radius reads as a sentence: "Within 150 km of both places". */}
      {radii.length > 0 && !tooShort && (
        <div role="radiogroup" aria-label="How far from both places" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12.5, color: MUTED }}>
          <span>Within</span>
          {radii.map((r) => {
            const active = r === activeRadius && !nothingAnywhere;
            // A radius that finds nothing is not offered.
            const empty = !radiusHasTrains(result?.counts, r);
            return (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={loading || empty}
                title={empty ? `No night train within ${r} km` : undefined}
                onClick={() => setRadiusKm(r)}
                style={{
                  font: 'inherit', fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: '6px 12px', cursor: loading || empty ? 'default' : 'pointer',
                  border: `1px solid ${active ? GREEN : 'rgba(20,40,30,0.10)'}`,
                  background: active ? '#e7f1ec' : (empty ? '#f7f9f7' : '#fff'),
                  color: active ? '#0e5c40' : (empty ? '#c3cac6' : MUTED),
                  textDecoration: empty ? 'line-through' : 'none',
                }}
              >
                {r} km
              </button>
            );
          })}
          <span>of both places</span>
        </div>
      )}
      {result?.note && !tooShort && <div style={{ fontSize: 12, fontWeight: 600, color: MUTED, marginTop: 8 }}>{result.note}.</div>}
      {noneDirect && !loading && (
        <div style={{ marginTop: 10, background: '#fbf3df', color: '#6b5310', border: '1px solid rgba(154,120,30,0.18)', borderRadius: 10, padding: '8px 11px', fontSize: 12.5, lineHeight: 1.4 }}>
          {nearbyNoDirectNote(fromCity, toCity)}
        </div>
      )}

      <div style={{ marginTop: 12, overflowY: 'auto', minHeight: 56, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {error ? (
          <div style={{ fontSize: 13, color: MUTED, padding: '8px 0' }}>Could not search night trains. Please close and try again.</div>
        ) : loading || !result ? (
          <div style={{ fontSize: 13, color: MUTED, padding: '8px 0' }}>Searching <Dots /></div>
        ) : tooShort ? (
          <div style={{ flex: 'none' }}>
            <div style={{ fontSize: 13, lineHeight: 1.45, color: INK }}>{nearbyTooShortNote(fromCity, toCity, result.legKm, result.rules.minLegKm)}</div>
            {rulesBlock(true)}
          </div>
        ) : options.length === 0 ? (
          <div style={{ flex: 'none' }}>
            <div style={{ fontSize: 13, lineHeight: 1.45, color: INK }}>
              {nothingAnywhere && widest != null ? nearbyEmptyNote(widest, result.radiiKm) : nearbyEmptyNote(result.radiusKm, result.radiiKm)}
            </div>
            {rulesBlock(false)}
          </div>
        ) : options.map((o) => {
          const isOpen = o.key === openKey;
          const runsChip = cardRunsNote(o.train);
          const runs = runsLine(o.train);
          const hedge = bedsHedge(o.train);
          const panelId = `nearby-${o.key}`;
          const toggle = () => setOpenKey(isOpen ? null : o.key);
          const farBoarding = o.train.boarding.tier !== 'in_town';
          const farArrival = o.train.arrival.tier !== 'in_town';
          // Read like a timetable (Paulo 2026-10-03: the old row squeezed the
          // stations, times and train into two lines): the train on top, then
          // departure ──ride time── arrival with each station under its time,
          // what is far from the trip's places right under that station, and
          // Details / Next at the foot.
          const offset = o.train.arrival.dayOffset ?? o.train.nightsOnBoard;
          const endNote = (far: boolean, change: boolean, km: number, city: string, byRoad?: boolean) => (far
            ? <span style={{ display: 'block', fontSize: 12, fontWeight: change ? 700 : 500, color: change ? '#9c7b22' : MUTED, marginTop: 2 }}>{nearbyDistanceNote(km, city, byRoad)}</span>
            : null);
          return (
            <div key={o.key} ref={isOpen ? openRowRef : undefined} style={{ flex: 'none', border: `1px solid ${isOpen ? '#cfe4d9' : HAIRLINE}`, borderRadius: 16, background: isOpen ? '#fbfdfc' : '#fff', padding: '13px 16px 11px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: NIGHT }}><span aria-hidden>🌙 </span>{o.train.displayName}</span>
                <span style={{ fontSize: 12.5, color: MUTED }}>{bedsChip(o.train)}</span>
                {runsChip && <span style={{ marginLeft: 'auto' }}><Chip tone={runsChip.tone === 'warn' ? 'warn' : 'plain'}>{runsChip.text}</Chip></span>}
              </div>

              {/* The text opens the details too, as the link under it does. */}
              <button type="button" onClick={toggle} aria-expanded={isOpen} aria-controls={panelId} aria-label={`${o.train.boarding.name} ${o.train.boarding.time} to ${o.train.arrival.name} ${o.train.arrival.time}, ${fmtRide(o.train.durationMin)} travelling. Details`} style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: 0, margin: '12px 0 0', font: 'inherit', textAlign: 'left', cursor: 'pointer' }}>
                <span style={{ display: 'grid', gridTemplateColumns: 'auto minmax(48px, 1fr) auto', alignItems: 'center', columnGap: 10 }}>
                  <span style={{ fontSize: 21, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{o.train.boarding.time}</span>
                  <span aria-hidden style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 20 }}>
                    <span style={{ position: 'absolute', left: 0, right: 0, top: '50%', borderTop: `2px dotted ${NIGHT}`, opacity: 0.35 }} />
                    <span style={{ position: 'absolute', left: -2, top: 'calc(50% - 3px)', width: 6, height: 6, borderRadius: '50%', background: NIGHT }} />
                    <span style={{ position: 'absolute', right: -2, top: 'calc(50% - 3px)', width: 6, height: 6, borderRadius: '50%', background: NIGHT }} />
                    <span style={{ position: 'relative', background: isOpen ? '#fbfdfc' : '#fff', padding: '0 8px', fontSize: 12, fontWeight: 600, color: MUTED, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtRide(o.train.durationMin)}</span>
                  </span>
                  <span style={{ fontSize: 21, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                    {o.train.arrival.time}
                    {offset > 0 && <sup style={{ fontSize: 11, fontWeight: 700, color: MUTED, marginLeft: 2 }}>+{offset}</sup>}
                  </span>
                </span>
                <span style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', columnGap: 16, marginTop: 7 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: INK, lineHeight: 1.3 }}>{o.train.boarding.name}</span>
                    {endNote(farBoarding, o.changeAtBoarding, o.fromKm, fromCity, o.fromByRoad)}
                  </span>
                  <span style={{ minWidth: 0, textAlign: 'right' }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: INK, lineHeight: 1.3 }}>{o.train.arrival.name}</span>
                    {endNote(farArrival, o.changeAtArrival, o.toKm, toCity, o.toByRoad)}
                  </span>
                </span>
              </button>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 11, paddingTop: 9, borderTop: `1px solid ${HAIRLINE}` }}>
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: '6px 0', font: 'inherit', fontSize: 12.5, fontWeight: 700, color: GREEN, cursor: 'pointer' }}
                >
                  {isOpen ? 'Hide details' : 'Details'} <Chevron open={isOpen} />
                </button>
                {/* "Next", not "Select": the train is added on the next view. The
                    train already in the trip keeps the button, disabled, with
                    "In your trip" beside it (Paulo 2026-10-03), so every card
                    ends the same way. */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  {o.current && <Chip tone="current">In your trip</Chip>}
                  <button
                    type="button"
                    onClick={o.current ? undefined : () => setReviewing(o)}
                    disabled={o.current}
                    aria-label={o.current ? `${o.train.displayName} is already in your trip` : `Next: ${o.train.displayName}`}
                    style={{ ...primaryButton, padding: '9px 20px', flex: 'none', cursor: o.current ? 'default' : 'pointer', opacity: o.current ? 0.35 : 1 }}
                  >
                    Next
                  </button>
                </span>
              </div>

              {isOpen && (
                <div id={panelId} style={{ borderTop: `1px solid ${HAIRLINE}`, marginTop: 6, paddingTop: 4 }}>
                  <Detail
                    label="Board at"
                    value={boardLine(o.train)}
                    note={farBoarding ? <>{nearbyDistanceNote(o.fromKm, fromCity, o.fromByRoad)}{o.changeAtBoarding && <>. {nearbyChangeNote('boarding', o.toBoardingMinutes, fromCity)}</>}</> : undefined}
                  />
                  <Detail
                    label="Get off at"
                    value={getOffLine(o.train)}
                    note={farArrival ? <>{nearbyDistanceNote(o.toKm, toCity, o.toByRoad)}{o.changeAtArrival && <>. {nearbyChangeNote('arrival', o.fromArrivalMinutes, toCity)}</>}</> : undefined}
                  />
                  <Detail label="On board" value={bedsList(o.train).join(' · ')} note={hedge} />
                  <Detail label="Runs" value={runs.text} tone={runs.tone === 'warn' ? 'warn' : undefined} />
                  <Detail label="Train" value={productLine(o.train)} />
                  <div style={{ marginTop: 4 }}><TrustMark train={o.train} /></div>
                </div>
              )}
            </div>
          );
        })}
        {!loading && !error && result && !tooShort && options.length > 0 && <div style={{ flex: 'none' }}>{rulesBlock(false)}</div>}
      </div>

      {hasEstimate(opened) && <div style={{ fontSize: 10.5, color: '#b3bbb6', marginTop: 8 }}>* estimated, by train</div>}
      <button
        type="button"
        onClick={onClose}
        style={{ width: '100%', marginTop: 11, background: '#f4f6f3', color: MUTED, border: '1px solid rgba(20,40,30,0.06)', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
      >
        Close
      </button>
    </>
  );

  const review = reviewing && result && (() => {
    const steps = nearbyReview(reviewing, fromCity, toCity);
    const runs = runsLine(reviewing.train);
    return (
      <>
        <div style={{ fontSize: 12.5, color: MUTED }}>This part of your trip becomes:</div>
        <div style={{ marginTop: 10, overflowY: 'auto', minHeight: 0 }}>
          {/* Places and how to get from one to the next, like the journey
              page's own rail: a dot per place, a line per ride. */}
          <div style={{ border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: '12px 13px' }}>
            {steps.map((step, i) => (step.kind === 'place' ? (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', columnGap: 10, alignItems: 'start' }}>
                <span aria-hidden style={{ justifySelf: 'center', marginTop: 4, width: 11, height: 11, borderRadius: '50%', boxSizing: 'border-box', background: step.isNew ? '#fff' : GREEN, border: `2px solid ${GREEN}` }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: INK }}>{step.name}</span>
                    {step.isNew && <Chip tone="current">{step.note}</Chip>}
                  </span>
                  {!step.isNew && step.note && <span style={{ display: 'block', fontSize: 12, color: MUTED, marginTop: 1 }}>{step.note}</span>}
                </span>
              </div>
            ) : (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr)', columnGap: 10 }}>
                <span aria-hidden style={{ justifySelf: 'center', width: 2, background: step.night ? NIGHT : '#cdd5d0', borderRadius: 2 }} />
                <span style={{ minWidth: 0, margin: '7px 0', padding: '8px 10px', borderRadius: 10, background: step.night ? '#ecebf7' : '#f4f6f3', color: step.night ? NIGHT : '#4b5a53' }}>
                  <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>
                    <span aria-hidden>{step.night ? '🌙' : getTransportEmoji('train')} </span>{step.text}
                  </span>
                  {step.note && <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{step.note}</span>}
                </span>
              </div>
            )))}
          </div>

          <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.45, color: MUTED }}>
            <span style={{ fontWeight: 700, color: INK }}>Replaces </span>
            {nearbyReplacesNote(result.replaces, fromCity, toCity, (m) => getModeLabel(m))}
          </div>
          {runs.tone === 'warn' && <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 600, color: '#9c7b22' }}>{runs.text}</div>}
          {result.replaces.hasUserData && <div style={{ marginTop: 6, fontSize: 12.5, fontWeight: 600, color: '#9c7b22' }}>{NEARBY_LOSS_NOTE}</div>}
        </div>

        {hasEstimate(reviewing) && <div style={{ fontSize: 10.5, color: '#b3bbb6', marginTop: 8 }}>* estimated, by train</div>}
        <div style={{ display: 'flex', gap: 9, marginTop: 11 }}>
          <button type="button" onClick={() => setReviewing(null)} disabled={adding} style={{ flex: 1, background: '#fff', border: '1px solid rgba(20,40,30,0.10)', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, color: MUTED, cursor: 'pointer' }}>Back</button>
          <button type="button" onClick={add} disabled={adding} style={{ flex: 1.4, background: GREEN, color: '#fff', border: 'none', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, cursor: adding ? 'default' : 'pointer', opacity: adding ? 0.7 : 1 }}>
            {adding ? <>Adding <Dots /></> : 'Add to my trip'}
          </button>
        </div>
      </>
    );
  })();

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Night trains nearby"
      onClick={(e) => { if (e.target === e.currentTarget && !adding) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2800, background: 'rgba(16,30,24,0.42)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <style>{'@keyframes lp-blink{0%,80%,100%{opacity:.2}40%{opacity:1}}.lp-dot{animation:lp-blink 1.2s infinite}.lp-d2{animation-delay:.2s}.lp-d3{animation-delay:.4s}'}</style>
      <div style={{ width: '100%', maxWidth: 540, maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 20, padding: '20px 20px 16px', boxShadow: '0 24px 60px rgba(20,40,30,0.25)' }}>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 16.5, color: INK }}>
          <span aria-hidden>🌙 </span>{reviewing ? 'Add this night train?' : 'Search night trains'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 12.5, fontWeight: 600, color: MUTED, margin: '3px 0 12px', minHeight: 18 }}>
          <span>{picking ? 'Which part of your trip?' : heading}</span>
          {switchable && (
            <button type="button" onClick={() => onPickLeg!(null)} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, fontWeight: 700, color: GREEN, cursor: 'pointer' }}>
              Another part of the trip
            </button>
          )}
        </div>
        {picking ? picker : (reviewing ? review : list)}
      </div>
    </div>,
    document.body
  );
};

export default NightTrainsNearbyDialog;
